'use strict';

/**
 * Product Gallery — Swiper: main + thumbs sync + lightbox
 * Requires: Swiper (already loaded globally)
 *
 * Layout (CSS Grid):
 *   col-1: thumbs (72px, vertical Swiper)
 *   col-2: main   (1fr, aspect-ratio: 1/1, Swiper fills via position:absolute)
 *
 * The main container has fixed aspect-ratio in CSS,
 * and .product-gallery__slider is position:absolute inside it,
 * so Swiper fills the reserved space without causing layout shift.
 */

const initProductGallery = () => {
    const mainEl = document.querySelector('#product-gallery-main');
    const thumbsEl = document.querySelector('#product-gallery-thumbs');
    const thumbsHostEl = thumbsEl?.closest('.product-gallery__thumbs');

    if (!mainEl) return;

    // Thumbs Swiper — vertical on desktop, horizontal on tablet/mobile
    const BP_TABLET = 1024;
    const isDesktop = () => window.innerWidth > BP_TABLET;

    let thumbsSwiper = null;
    if (thumbsEl) {
        thumbsSwiper = new Swiper(thumbsEl, {
            spaceBetween: 8,
            slidesPerView: 'auto',
            direction: isDesktop() ? 'vertical' : 'horizontal',
            watchSlidesProgress: true,
            watchOverflow: true,
            freeMode: true,
            navigation: {
                prevEl: '.product-gallery__thumbs-nav--up',
                nextEl: '.product-gallery__thumbs-nav--down',
            },
            // Init mousewheel always so event listeners are attached.
            // Toggle enable/disable on breakpoint change instead of re-creating.
            mousewheel: {
                forceToAxis: true,
                releaseOnEdges: true,
                sensitivity: 0.9,
            },
        });

        // Disable on mobile/tablet after init (listeners already attached).
        if (!isDesktop()) {
            try { thumbsSwiper.mousewheel?.disable(); } catch (e) {}
        }
    }

    // Hide nav arrows when all thumbs fit (nothing to scroll)
    const navUp   = document.querySelector('.product-gallery__thumbs-nav--up');
    const navDown = document.querySelector('.product-gallery__thumbs-nav--down');
    const syncNavVisibility = () => {
        if (!thumbsSwiper || !navUp || !navDown) return;
        const hidden = thumbsSwiper.isLocked;
        navUp.hidden   = hidden;
        navDown.hidden = hidden;
    };

    // Sync thumbs column height to main image height (desktop only)
    const syncThumbsHeight = () => {
        if (!thumbsEl || !thumbsHostEl) return;
        if (!isDesktop()) {
            thumbsHostEl.style.height = '';
            thumbsEl.style.height = '';
            syncNavVisibility();
            return;
        }
        const mainHeight = mainEl.clientHeight;
        if (mainHeight > 0) {
            const px = `${mainHeight}px`;
            thumbsHostEl.style.height = px;
            thumbsEl.style.height = px;
        }
        syncNavVisibility();
    };

    // Main gallery Swiper
    const mainSwiper = new Swiper(mainEl, {
        spaceBetween: 0,
        slidesPerView: 1,
        autoHeight: true,
        observer: true,
        observeParents: true,
        navigation: {
            prevEl: '.product-gallery__nav--prev',
            nextEl: '.product-gallery__nav--next',
        },
        thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
        on: {
            slideChange(s) {
                // Обновляем счётчик в product-gallery__counter через productPage() Alpine scope.
                window.dispatchEvent(new CustomEvent('gallery:slide', { detail: s.activeIndex + 1 }));
            },
        },
    });

    syncThumbsHeight();

    // On resize: rebuild thumbsSwiper if direction needs to change
    let prevDesktop = isDesktop();
    window.addEventListener('resize', () => {
        const nowDesktop = isDesktop();
        if (nowDesktop !== prevDesktop && thumbsSwiper) {
            prevDesktop = nowDesktop;
            const dir = nowDesktop ? 'vertical' : 'horizontal';
            try { thumbsSwiper.changeDirection(dir, true); } catch (e) {}
            try {
                if (nowDesktop) thumbsSwiper.mousewheel?.enable();
                else thumbsSwiper.mousewheel?.disable();
            } catch (e) {}
        }
        syncThumbsHeight();
    });

    // Desktop wheel fallback in case Swiper mousewheel module isn't active in build.
    if (thumbsHostEl) {
        thumbsHostEl.addEventListener('wheel', (e) => {
            if (window.innerWidth <= 1024 || !thumbsSwiper) return;
            e.preventDefault();
            if (e.deltaY > 0) {
                thumbsSwiper.slideNext();
            } else if (e.deltaY < 0) {
                thumbsSwiper.slidePrev();
            }
        }, { passive: false });
    }

    // Fancybox v5 requires explicit bind — does not auto-detect data-fancybox
    if (typeof Fancybox !== 'undefined') {
        Fancybox.bind('[data-fancybox="product-gallery"]', {
            animated: true,
            groupAll: false,
            Images: {
                Panzoom: {
                    maxScale: 3,
                },
            },
            Thumbs: {
                type: 'classic',
            },
            contentClick: 'toggleZoom',
        });

        // Видео-слайд открываем через Fancybox.show() — явно задаём type/size, чтобы
        // iframe не обрезался и чтобы YouTube/Vimeo детектились video-плагином.
        document.querySelectorAll('.product-gallery__video-slide').forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const src = link.getAttribute('href');
                if (!src) return;
                Fancybox.show([{
                    src,
                    type: 'iframe',
                    width: 1280,
                    height: 720,
                    caption: link.getAttribute('data-caption') || '',
                }]);
            });
        });
    }

    // Click on "+N more" thumb → open Fancybox at that slide
    const moreThumb = document.querySelector('.product-gallery__thumb--more');
    if (moreThumb) {
        moreThumb.addEventListener('click', () => {
            const link = mainEl.querySelectorAll('[data-fancybox="product-gallery"]');
            const idx = mainSwiper.activeIndex || 0;
            if (link[idx]) link[idx].click();
        });
    }

    // Expose for SKU switching
    window.__productGallery = {
        mainSwiper,
        thumbsSwiper,
        slideTo(imageId) {
            const images = window.__PRODUCT__?.images || [];
            const idx = images.findIndex(img => img.id === imageId);
            if (idx >= 0) mainSwiper.slideTo(idx);
        },
    };
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductGallery);
} else {
    initProductGallery();
}
